import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { PageHeader, EmptyState, StatusBadge, formatIDR } from "../components/Shared";
import { PayDialog } from "../components/programs/PayDialog";
import { ImageUpload } from "../components/ImageUpload";
import { ProofImage } from "../components/ProofImage";
import { JOURNEY } from "../constants/testIds";
import { Ticket, GraduationCap, Users } from "lucide-react";

export default function MyJourney() {
  const { user } = useAuth();
  const { t } = useLang();
  const isMember = user?.role === "MEMBER";
  const isCoach = user?.role === "COACH";
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [apps, setApps] = useState([]);
  const [members, setMembers] = useState([]);
  const [motivation, setMotivation] = useState("");
  const [proofFileId, setProofFileId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, p, a] = await Promise.all([api.get("/enrollments/me"), api.get("/payments/me"), api.get("/coach-candidates/me")]);
      setEnrollments(e.data.data); setPayments(p.data.data); setApps(a.data.data);
      if (isCoach) setMembers((await api.get("/coach/members")).data.data);
    } catch (err) { toast.error(extractError(err).message); }
  }, [isCoach]);

  useEffect(() => { load(); }, [load]);

  const apply = async (e) => {
    e.preventDefault();
    if (!proofFileId) return toast.error(t("coach.proofRequired"));
    setBusy(true);
    try {
      await api.post("/coach-candidates/apply", { motivation, membership_proof_file_id: proofFileId });
      toast.success(t("coach.applied"));
      setMotivation("");
      setProofFileId(null);
      load();
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  const openApp = apps.find((a) => ["IN_QUALIFICATION", "WAITING_REVIEW"].includes(a.status));

  return (
    <div data-testid={JOURNEY.page} className="space-y-8">
      <PageHeader eyebrow={t("journey.eyebrow")} title={t("journey.title")} subtitle={t("journey.subtitle")}>
        <Link to="/programs"><Button className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold h-11 px-5">{t("journey.browse")}</Button></Link>
      </PageHeader>

      {paying && <PayDialog batch={paying.batch} enrollment={paying} onClose={() => { setPaying(null); load(); }} />}

      {isCoach && (
        <section className="space-y-3">
          <h2 className="font-[Outfit] text-lg font-bold text-white flex items-center gap-2"><Users className="h-4 w-4 text-[#10B981]" /> {t("journey.myMembers")}</h2>
          {members.length === 0 ? <EmptyState testId={JOURNEY.membersEmpty} text={t("journey.noMembers")} /> : members.map((m) => (
            <Card key={m.enrollment_id} data-testid={`coach-member-${m.enrollment_id}`} className="bg-[#161923] border-white/5">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                <div><span className="text-white font-medium">{m.member_name}</span> <span className="text-slate-500 text-xs">{m.member_email}</span><div className="text-xs text-slate-500">{m.batch?.program_name} · {m.batch?.name}</div></div>
                <div className="flex items-center gap-3"><span className="font-mono text-emerald-300 text-xs">{m.participant_code}</span><StatusBadge status={m.status} /></div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}

      <section className="space-y-3">
        <h2 className="font-[Outfit] text-lg font-bold text-white flex items-center gap-2"><Ticket className="h-4 w-4 text-[#10B981]" /> {t("journey.enrollments")}</h2>
        {enrollments.length === 0 ? <EmptyState testId={JOURNEY.enrollEmpty} text={t("journey.noEnrollments")} /> : enrollments.map((e) => (
          <Card key={e.enrollment_id} data-testid={`enrollment-row-${e.enrollment_id}`} className="bg-[#161923] border-white/5">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-white">{e.batch?.program_name} · {e.batch?.name}</div>
                <div className="text-xs text-slate-500 mt-1">{e.batch?.start_date} → {e.batch?.end_date} · Coach: <span className="text-slate-300">{e.coach?.full_name || t("programs.noCoach")}</span></div>
                <div className="text-xs mt-1"><span className="text-slate-500">{t("payments.totalToPay")}:</span> <span data-testid={`enrollment-total-${e.enrollment_id}`} className="text-[#F59E0B] font-semibold">{formatIDR(e.total_amount)}</span></div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span data-testid={`participant-code-${e.enrollment_id}`} className="font-mono text-sm font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-1.5">{e.participant_code}</span>
                <StatusBadge status={e.status} testId={`enrollment-status-${e.enrollment_id}`} />
                {isMember && e.status === "PENDING_PAYMENT" && !(e.latest_payment && e.latest_payment.status === "PENDING") && (
                  <Button data-testid={`enrollment-pay-${e.enrollment_id}`} size="sm" onClick={() => setPaying(e)} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-9">{t("payments.submit")}</Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="font-[Outfit] text-lg font-bold text-white">{t("journey.payments")}</h2>
        {payments.length === 0 ? <EmptyState testId={JOURNEY.payEmpty} text={t("journey.noPayments")} /> : payments.map((p) => (
          <Card key={p.payment_id} data-testid={`my-payment-${p.payment_id}`} className="bg-[#161923] border-white/5">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="text-slate-300">{p.batch?.name} · <span className="font-mono text-xs text-emerald-300">{p.participant_code}</span> · <span className="text-[#F59E0B] font-semibold">{formatIDR(p.total_amount)}</span>{p.rejection_reason && <span className="text-red-300 ml-2">— {p.rejection_reason}</span>}</div>
              <StatusBadge status={p.status} />
            </CardContent>
          </Card>
        ))}
      </section>

      {isMember && (
        <section className="space-y-3">
          <h2 className="font-[Outfit] text-lg font-bold text-white flex items-center gap-2"><GraduationCap className="h-4 w-4 text-[#10B981]" /> {t("coach.becomeCoach")}</h2>
          <Card className="bg-gradient-to-br from-[#10121B] to-[#161923] border-white/5">
            <CardContent className="p-6 space-y-4">
              {apps.map((a) => (
                <div key={a.candidate_id} data-testid={`my-application-${a.candidate_id}`} className="flex items-center justify-between text-sm text-slate-300 gap-3">
                  <span>{new Date(a.created_at).toLocaleDateString()} · {t("coach.progress")} {a.qualification_progress ?? 0}% {a.review_note && <span className="text-slate-500">— {a.review_note}</span>}</span>
                  <div className="flex items-center gap-2">
                    {a.membership_proof_file_id && (
                      <ProofImage fileId={a.membership_proof_file_id} testId={`my-application-proof-${a.candidate_id}`} className="h-10 w-10" />
                    )}
                    <StatusBadge status={a.status} testId={`my-application-status-${a.candidate_id}`} />
                  </div>
                </div>
              ))}
              {openApp ? (
                <div className="space-y-3">
                  <p data-testid={JOURNEY.applyPending} className="text-sm text-amber-300">{openApp.status === "WAITING_REVIEW" ? t("coach.pendingInfo") : t("coach.qualifyingInfo")}</p>
                  <p data-testid="journey-qualification-note" className="text-xs text-slate-500">{t("coach.programAfterApproval")}</p>
                </div>
              ) : (
                <form onSubmit={apply} className="space-y-3">
                  <p className="text-sm text-slate-400">{t("coach.applyInfo")}</p>
                  <Label className="text-slate-300 text-sm">{t("coach.motivation")}</Label>
                  <textarea data-testid={JOURNEY.applyMotivation} value={motivation} onChange={(e) => setMotivation(e.target.value)} rows={3} minLength={10} required className="w-full rounded-md bg-[#1C202E] border border-white/10 text-white text-sm p-3 outline-none focus:border-[#FF3B30]/60" />
                  <ImageUpload endpoint="/uploads/coach-proof" testId="coach-proof-input" label={t("coach.proof")}
                    hint={t("coach.proofHint")} onUploaded={setProofFileId} />
                  <div className="flex justify-end">
                    <Button data-testid={JOURNEY.applySubmit} type="submit" disabled={busy} className="bg-[#10B981] hover:bg-emerald-500 text-white font-semibold">{busy ? t("common.loading") : t("coach.apply")}</Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
